import { auth } from '../lib/firebase';

export interface PricingPlan {
  plan_id: string;
  tier: 'pro_monthly' | 'pro_semi_annual' | 'pro_yearly' | 'pro_plus_monthly' | 'pro_plus_semi_annual' | 'pro_plus_yearly';
  name: string;
  price: number;
  billing_cycle: 'monthly' | 'semi_annual' | 'yearly';
  currency: string;
  features: {
    storage_gb: number;
    unlimited_students: boolean;
    unlimited_courses: boolean;
    dashboard: boolean;
    seo_optimization?: boolean;
    sale_page_builder?: boolean;
  };
}

export interface AddonPricing {
  addon_id: string;
  name: string;
  storage_gb: number;
  price_monthly: number;
  price_yearly: number;
  currency: string;
}

export const getOmisePublicKey = async (): Promise<string> => {
  const response = await fetch('https://getomisepublickey-191275868600.us-central1.run.app');
  if (!response.ok) {
    throw new Error('Failed to get Omise public key');
  }
  const data = await response.json();
  return data.publicKey;
};

export const getPricingPlans = async (): Promise<PricingPlan[]> => {
  const response = await fetch('https://getpricing-191275868600.us-central1.run.app');
  if (!response.ok) {
    throw new Error('Failed to get pricing plans');
  }
  const data = await response.json();
  return data.plans;
};

export const getAddonPricing = async (billingCycle: 'monthly' | 'yearly'): Promise<AddonPricing[]> => {
  const response = await fetch('https://getpricing-191275868600.us-central1.run.app', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ type: 'addons', billing_cycle: billingCycle }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to get addon pricing');
  }
  
  const data = await response.json();
  
  if (!data.success) {
    throw new Error('Failed to get addon pricing');
  }
  
  return data.addons;
};

export const createSubscription = async (
  omiseToken: string,
  planId: string,
  billingCycle: 'monthly' | 'yearly'
): Promise<{
  success: boolean;
  subscription_id: string;
  charge_id: string;
  status: string;
  authorize_uri?: string;
}> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  const token = await user.getIdToken();
  
  const response = await fetch('https://createomisesubscription-mgs3rglu4q-uc.a.run.app', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      omise_token: omiseToken,
      plan_id: planId,
      billing_cycle: billingCycle,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create subscription');
  }
  
  return await response.json();
};

export const createAddon = async (
  addonId: string,
  billingCycle: 'monthly' | 'yearly'
): Promise<{
  success: boolean;
  addon_id: string;
  charge_id: string;
  status: string;
  authorize_uri?: string;
}> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  const token = await user.getIdToken();
  
  const response = await fetch('https://createomiseaddon-191275868600.us-central1.run.app', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      addon_id: addonId,
      billing_cycle: billingCycle,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create addon');
  }
  
  return await response.json();
};

export const cancelSubscription = async (): Promise<{
  success: boolean;
  message: string;
  period_end: string;
}> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  const token = await user.getIdToken();
  
  const response = await fetch('https://cancelomisesubscription-191275868600.us-central1.run.app', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cancel subscription');
  }
  
  return await response.json();
};

declare global {
  interface Window {
    Omise: any;
  }
}

export const loadOmiseScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.Omise) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.omise.co/omise.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Omise script'));
    document.head.appendChild(script);
  });
};

export const createOmiseToken = async (
  publicKey: string,
  cardData: {
    name: string;
    number: string;
    expiration_month: string;
    expiration_year: string;
    security_code: string;
  }
): Promise<string> => {
  await loadOmiseScript();
  
  window.Omise.setPublicKey(publicKey);
  
  return new Promise((resolve, reject) => {
    window.Omise.createToken('card', cardData, (statusCode: number, response: any) => {
      if (statusCode === 200) {
        resolve(response.id);
      } else {
        reject(new Error(response.message || 'Failed to create token'));
      }
    });
  });
};

export const createPromptPayToken = async (
  publicKey: string,
  amount: number
): Promise<string> => {
  await loadOmiseScript();
  
  window.Omise.setPublicKey(publicKey);
  
  return new Promise((resolve, reject) => {
    window.Omise.createSource('promptpay', { amount: amount * 100, currency: 'THB' }, (statusCode: number, response: any) => {
      if (statusCode === 200) {
        resolve(response.id);
      } else {
        reject(new Error(response.message || 'Failed to create PromptPay source'));
      }
    });
  });
};
